import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Megaphone, Gift, MessageCircle } from "lucide-react";
import { useStrikePrices, strikeCls, UPGRADE_WA } from "@/lib/pricing";

export const CompanyPlan = () => {
  const [st, setSt] = useState(null);
  const [title, setTitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [busy, setBusy] = useState(false);
  const strike = useStrikePrices();

  const load = () =>
    api.get("/company/status").then((r) => {
      setSt(r.data);
      if (r.data.banner) { setTitle(r.data.banner.title || ""); setTagline(r.data.banner.tagline || ""); }
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!st) return null;

  const startFree = async () => {
    setBusy(true);
    try { await api.post("/company/free-launch"); toast.success("Free launch month activated!"); load(); window.dispatchEvent(new Event("mc-status-changed")); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start free offer"); }
    finally { setBusy(false); }
  };

  const saveBanner = async () => {
    try { await api.post("/company/banner", { title, tagline }); toast.success("Home-page banner saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save banner"); }
  };

  if (st.subscribed && st.free_launch) {
    return (
      <div className="rounded-2xl border border-[#25D366]/40 bg-[#25D366]/5 p-5 mb-6" data-testid="company-free-launch-active">
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-5 h-5 text-[#25D366]" />
          <h3 className="font-heading font-extrabold text-lg text-white">Free Launch Offer active</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#25D366] text-black font-bold">FREE</span>
        </div>
        <p className="text-sm text-[#C9CFDA]">
          Contact unlocks included · {st.downloads_today}/{st.download_limit} CVs downloaded today.
          {st.subscription_expiry ? ` Free until ${String(st.subscription_expiry).slice(0, 10)}.` : ""}
        </p>
        <div className="mt-3 inline-flex items-baseline gap-2 rounded-xl bg-black/30 border border-[#25D366]/40 px-4 py-2" data-testid="company-card-days-left">
          <span className="font-heading font-extrabold text-3xl text-[#25D366]">{st.free_launch_days_left}</span>
          <span className="text-sm font-bold text-white">day{st.free_launch_days_left === 1 ? "" : "s"} left in your free month</span>
        </div>
      </div>
    );
  }

  if (st.subscribed) {
    return (
      <div className="rounded-2xl border border-[#DFFF00]/40 bg-[#DFFF00]/5 p-5 mb-6" data-testid="company-plan-active">
        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-5 h-5 text-[#DFFF00]" />
          <h3 className="font-heading font-extrabold text-lg text-white">Annual Plan active</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DFFF00] text-black font-bold">SUBSCRIBED</span>
        </div>
        <p className="text-sm text-[#C9CFDA] mb-4">
          Unlimited free contact unlocks · {st.downloads_today}/{st.download_limit} Standard CVs downloaded today · home-page banner live.
          {st.subscription_expiry ? ` Renews ${String(st.subscription_expiry).slice(0, 10)}.` : ""}
        </p>
        <div className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="flex items-center gap-2 mb-2 text-white font-semibold text-sm"><Megaphone className="w-4 h-4 text-[#FF5A00]" /> Your home-page banner</div>
          <Input className="bg-black/30 border-white/10 mb-2" placeholder="Banner title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="banner-title-input" />
          <Input className="bg-black/30 border-white/10 mb-3" placeholder="Tagline (optional)" value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="banner-tagline-input" />
          <Button className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-full" onClick={saveBanner} disabled={!title} data-testid="save-banner-btn">Save banner</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0A0B12] p-5 sm:p-6 mb-6" data-testid="company-plan-upgrade">
      <div className="rounded-xl border-2 border-[#FF9933]/60 bg-gradient-to-br from-[#FF9933]/15 to-transparent p-5" data-testid="launch-offer-card">
        <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest text-[#C9CFDA] font-semibold">
          <Crown className="w-4 h-4 text-[#FF9933]" /> Company plan
        </div>
        <div
          className={`font-heading font-extrabold text-4xl sm:text-5xl text-white ${strikeCls(strike, "decoration-[4px]")}`}
          data-testid="launch-original-price"
        >
          ₹9,999/month
        </div>
        {st.free_launch_ended ? (
          <>
            <p className="mt-3 text-xl sm:text-2xl font-extrabold text-[#FF3B30]" data-testid="company-card-ended-text">Your free month has ended</p>
            <p className="mt-2 text-sm text-[#C9CFDA]">Upgrade to keep unlocking crew contacts and downloading CVs.</p>
            <Button asChild className="mt-5 w-full sm:w-auto rounded-full bg-[#25D366] hover:bg-[#1da851] text-black font-bold text-base h-12 px-8 gap-2" data-testid="company-card-upgrade-btn">
              <a href={UPGRADE_WA} target="_blank" rel="noreferrer"><MessageCircle className="w-5 h-5" /> Upgrade — contact us</a>
            </Button>
          </>
        ) : (
        <>
        <p className="mt-3 text-xl sm:text-2xl font-extrabold text-[#25D366]" data-testid="launch-offer-text">
          FREE for 1 month — Limited Launch Offer
        </p>
        <p className="mt-2 text-sm text-[#C9CFDA]">Contact unlocks included · 10 CV downloads per day · no payment needed</p>
        <Button
          className="mt-5 w-full sm:w-auto rounded-full bg-[#25D366] hover:bg-[#1da851] text-black font-bold text-base h-12 px-8 gap-2"
          disabled={busy}
          onClick={startFree}
          data-testid="get-started-free-btn"
        >
          <Gift className="w-5 h-5" />
          {busy ? "Activating..." : "Get Started Free"}
        </Button>
        </>
        )}
      </div>
    </div>
  );
};
